import {
  getAccountId,
  getAuthToken,
  getUserToken,
  handleUnauthorized,
  invalidateAppToken,
} from './auth';
import { apiUrl, HOST_CONFIG, type ApiHost } from './config';
import { ApiError } from './errors';
import { apiTransport } from './transport';

export type RequestPayload = Record<string, unknown>;
export type RequestEncoding = 'form' | 'json';

export interface RequestOptions {
  host: ApiHost;
  path: string;
  /** Defaults to POST when a payload is present, GET otherwise — as the legacy client did. */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** Sent as the body on POST/PUT, or as the query string on GET. */
  data?: RequestPayload;
  /** Always appended to the query string, whatever the method. */
  query?: RequestPayload;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skips the auth headers, for endpoints that must stay anonymous. */
  anonymous?: boolean;
  /** Optional per-request override for host default body encoding. */
  encoding?: RequestEncoding;
}

function toFieldValue(value: unknown): string {
  switch (typeof value) {
    case 'string':
      return value;
    case 'number':
    case 'boolean':
    case 'bigint':
      return value.toString();
    default:
      // jQuery sent null as an empty field, and endpoints such as the sports menu
      // rely on the key being present.
      return value instanceof Date ? value.toISOString() : '';
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !(value instanceof Date);
}

/**
 * Reproduces jQuery's `$.param` deep serialisation, which the form-encoded hosts
 * were built against: nested objects become `Filters[AccountGuid]` and arrays
 * become `Categories[]`. Flattening these any other way binds to an empty model
 * server-side, which surfaces as a 500 rather than a validation error.
 */
function appendParam(params: URLSearchParams, prefix: string, value: unknown): void {
  if (value === undefined) return;

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      appendParam(params, isPlainObject(item) ? `${prefix}[${index}]` : `${prefix}[]`, item);
    });
    return;
  }

  if (isPlainObject(value)) {
    for (const [key, nested] of Object.entries(value)) {
      appendParam(params, `${prefix}[${key}]`, nested);
    }
    return;
  }

  params.append(prefix, toFieldValue(value));
}

function toQueryString(payload: RequestPayload): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(payload)) {
    appendParam(params, key, value);
  }
  return params.toString();
}

interface AuthAttachment {
  headers: Record<string, string>;
  /** True only when the request carries the player's bearer token, not the app token. */
  usedPlayerToken: boolean;
}

async function authHeaders(host: ApiHost): Promise<AuthAttachment> {
  if (HOST_CONFIG[host].auth === 'account') {
    return { headers: { AccountId: await getAccountId() }, usedPlayerToken: false };
  }

  const userToken = getUserToken();
  if (userToken) {
    return { headers: { Authorization: `Bearer ${userToken}` }, usedPlayerToken: true };
  }

  return { headers: { Authorization: `Bearer ${await getAuthToken()}` }, usedPlayerToken: false };
}

/**
 * Single entry point for HTTP calls. It applies the per-host body encoding and
 * authentication scheme, and turns a rejected session into one logout path.
 */
export async function apiRequest<T>(options: RequestOptions): Promise<T> {
  const { host, path, data, query, headers = {}, signal, anonymous = false, encoding } = options;
  const method = options.method ?? (data ? 'POST' : 'GET');
  const sendsBody = method !== 'GET' && method !== 'DELETE';
  const requestEncoding = encoding ?? HOST_CONFIG[host].encoding;

  const search = toQueryString({
    ...(sendsBody ? {} : (data ?? {})),
    ...(query ?? {}),
  });

  const url = `${apiUrl(host, path)}${search ? `?${search}` : ''}`;
  const requestHeaders: Record<string, string> = { Accept: 'application/json', ...headers };

  let body: string | undefined;
  if (sendsBody && data) {
    if (requestEncoding === 'json') {
      requestHeaders['Content-Type'] = 'application/json';
      body = JSON.stringify(data);
    } else {
      requestHeaders['Content-Type'] = 'application/x-www-form-urlencoded';
      body = toQueryString(data);
    }
  }

  let usedPlayerToken = false;
  if (!anonymous) {
    const auth = await authHeaders(host);
    Object.assign(requestHeaders, auth.headers);
    usedPlayerToken = auth.usedPlayerToken;
  }

  let response: {
    status: number;
    statusText: string;
    data: unknown;
    headers: Record<string, string | string[] | undefined>;
  };
  try {
    response = await apiTransport.request({
      url,
      method,
      headers: requestHeaders,
      ...(body === undefined ? {} : { data: body }),
      ...(signal ? { signal } : {}),
      responseType: 'text',
      transformResponse: [(raw) => raw],
      validateStatus: () => true,
    });
  } catch (cause) {
    // An aborted request is the caller's intent, not a transport failure.
    if (
      (cause instanceof DOMException && cause.name === 'AbortError') ||
      (typeof cause === 'object' &&
        cause !== null &&
        'code' in cause &&
        (cause as { code?: unknown }).code === 'ERR_CANCELED')
    ) {
      throw new DOMException('The request was aborted.', 'AbortError');
    }
    throw new ApiError({ status: 0, statusText: 'Network error', url, body: String(cause) });
  }

  const payload = parseBody(response.data, response.headers['content-type']);

  if (response.status < 200 || response.status >= 300) {
    const error = new ApiError({
      status: response.status,
      statusText: response.statusText,
      url,
      body: payload,
    });
    if (error.isUnauthorized) {
      if (usedPlayerToken) handleUnauthorized();
      else if (!anonymous && HOST_CONFIG[host].auth === 'bearer') invalidateAppToken();
    }
    throw error;
  }

  return payload as T;
}

function parseBody(rawBody: unknown, contentTypeHeader: string | string[] | undefined): unknown {
  const text = typeof rawBody === 'string' ? rawBody : rawBody == null ? '' : String(rawBody);
  if (text === '') return undefined;

  const contentType = Array.isArray(contentTypeHeader)
    ? contentTypeHeader.join(';')
    : contentTypeHeader ?? '';
  if (contentType.includes('json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  // Several endpoints return JSON without saying so, so try anyway.
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/** Convenience wrappers so endpoint modules read as data access, not transport. */
export const api = {
  get: <T>(host: ApiHost, path: string, query?: RequestPayload, signal?: AbortSignal) =>
    apiRequest<T>({
      host,
      path,
      method: 'GET',
      ...(query ? { query } : {}),
      ...(signal ? { signal } : {}),
    }),

  post: <T>(
    host: ApiHost,
    path: string,
    data?: RequestPayload,
    signal?: AbortSignal,
    options?: { encoding?: RequestEncoding },
  ) =>
    apiRequest<T>({
      host,
      path,
      method: 'POST',
      ...(data ? { data } : {}),
      ...(signal ? { signal } : {}),
      ...(options?.encoding ? { encoding: options.encoding } : {}),
    }),
};
